import { FirebaseAuthentication, type User } from '@capacitor-firebase/authentication';
import { verifyPasswordResetCode, type Auth } from 'firebase/auth';
import type {
  AuthUser,
  FirebaseAuthService,
  FirebaseWebConfig,
  PhoneIntent,
  SentPhoneCode,
} from './types';
import { createJsAuth, noCurrentUser } from './web';

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    phoneNumber: user.phoneNumber,
    emailVerified: user.emailVerified,
    providerIds: user.providerData.map((p) => p.providerId),
  };
}

function authError(code: string, message: string): Error {
  return Object.assign(new Error(message), { code });
}

function confirmCode(verificationId: string, phoneNumber: string, intent: PhoneIntent) {
  return async (code: string) => {
    try {
      const { user } = await FirebaseAuthentication.confirmVerificationCode({
        verificationId,
        verificationCode: code,
      });
      return toAuthUser(user)!;
    } catch (err) {
      // Android auto-retrieval may have already signed in with this code, expiring the session.
      // Not for reauthenticate: the user was signed in as this number before the code was sent.
      const expired = ['auth/code-expired', 'auth/session-expired'].includes(
        (err as { code?: string }).code ?? ''
      );
      if (expired && intent === 'signIn') {
        const { user } = await FirebaseAuthentication.getCurrentUser();
        if (user?.phoneNumber === phoneNumber) return toAuthUser(user)!;
      }
      throw err;
    }
  };
}

// The plugin reports phone sign-in through events, not the call's result. The listeners stay
// registered so a late event (e.g. Android auto-retrieval) is consumed instead of replayed later.
let pendingPhone: {
  resolve: (sent: SentPhoneCode) => void;
  reject: (err: Error) => void;
  phoneNumber: string;
  intent: PhoneIntent;
} | null = null;
let phoneListeners: Promise<unknown> | null = null;

function listenForPhoneEvents() {
  const settle = (fn: (pending: NonNullable<typeof pendingPhone>) => void) => {
    const pending = pendingPhone;
    pendingPhone = null;
    if (pending) fn(pending);
  };
  phoneListeners ??= Promise.all([
    FirebaseAuthentication.addListener('phoneCodeSent', ({ verificationId }) =>
      settle((p) =>
        p.resolve({
          verifiedUser: null,
          confirm: confirmCode(verificationId, p.phoneNumber, p.intent),
        })
      )
    ),
    FirebaseAuthentication.addListener('phoneVerificationCompleted', ({ user }) =>
      settle((p) =>
        p.resolve({
          verifiedUser: toAuthUser(user),
          confirm: async () => toAuthUser(user)!,
        })
      )
    ),
    // The plugin's failure event carries only a message, no error code.
    FirebaseAuthentication.addListener('phoneVerificationFailed', () =>
      settle((p) =>
        p.reject(authError('auth/phone-verification-failed', 'Phone verification failed'))
      )
    ),
  ]);
  return phoneListeners;
}

export class NativeFirebaseAuth implements FirebaseAuthService {
  readonly platform = 'native' as const;
  // Only for verifyPasswordResetCode, which the plugin lacks and which needs no session.
  private readonly jsAuth: () => Auth;

  constructor(config: FirebaseWebConfig) {
    this.jsAuth = createJsAuth(config, 'memory');
  }

  async currentUser() {
    return toAuthUser((await FirebaseAuthentication.getCurrentUser()).user);
  }

  async reload() {
    if (!(await this.currentUser())) return null;
    await FirebaseAuthentication.reload();
    return this.currentUser();
  }

  async getIdToken(forceRefresh = false) {
    if (!(await this.currentUser())) throw noCurrentUser();
    return (await FirebaseAuthentication.getIdToken({ forceRefresh })).token;
  }

  signOut() {
    return FirebaseAuthentication.signOut();
  }

  async deleteCurrentUser() {
    if (!(await this.currentUser())) throw noCurrentUser();
    await FirebaseAuthentication.deleteUser();
  }

  async signInWithEmail(email: string, password: string) {
    return toAuthUser(
      (await FirebaseAuthentication.signInWithEmailAndPassword({ email, password })).user
    )!;
  }

  async createUserWithEmail(email: string, password: string) {
    return toAuthUser(
      (await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password })).user
    )!;
  }

  sendEmailVerification() {
    return FirebaseAuthentication.sendEmailVerification();
  }

  sendPasswordResetEmail(email: string) {
    return FirebaseAuthentication.sendPasswordResetEmail({ email });
  }

  verifyPasswordResetCode(oobCode: string) {
    return verifyPasswordResetCode(this.jsAuth(), oobCode);
  }

  confirmPasswordReset(oobCode: string, newPassword: string) {
    return FirebaseAuthentication.confirmPasswordReset({ oobCode, newPassword });
  }

  applyActionCode(oobCode: string) {
    return FirebaseAuthentication.applyActionCode({ oobCode });
  }

  verifyBeforeUpdateEmail(newEmail: string, continueUrl: string) {
    return FirebaseAuthentication.verifyBeforeUpdateEmail({
      newEmail,
      actionCodeSettings: { url: continueUrl, handleCodeInApp: true },
    });
  }

  // The plugin has no reauthenticate; signing in again as the same email refreshes the login time.
  async reauthenticateWithPassword(password: string) {
    const user = await this.currentUser();
    if (!user?.email) throw noCurrentUser();
    await FirebaseAuthentication.signInWithEmailAndPassword({ email: user.email, password });
  }

  async sendPhoneCode(phoneNumber: string, intent: PhoneIntent): Promise<SentPhoneCode> {
    if (intent === 'updatePhone') {
      // The plugin cannot change a user's phone number.
      throw authError(
        'auth/operation-not-supported-in-this-environment',
        'Changing the phone number is not supported in the app'
      );
    }
    // As with reauthenticateWithPassword: the plugin has no reauthenticate, and signing in
    // again as the same number refreshes the login time. Any other number would sign in as
    // someone else.
    if (intent === 'reauthenticate' && (await this.currentUser())?.phoneNumber !== phoneNumber) {
      throw noCurrentUser();
    }
    await listenForPhoneEvents();
    const result = new Promise<SentPhoneCode>((resolve, reject) => {
      pendingPhone?.reject(authError('auth/cancelled', 'Replaced by a newer request'));
      pendingPhone = { resolve, reject, phoneNumber, intent };
    });
    try {
      await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber });
    } catch (err) {
      pendingPhone = null;
      throw err;
    }
    return result;
  }
}
